import React, { useCallback, useContext, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { toast } from "react-hot-toast";
import { useAuth } from "../../../contexts/auth";
import {
  ROLE_STYLIST,
  ROLE_SALON_ADMIN,
  ROLE_SYSTEM_ADMIN,
  ROLE_JDTECH_ADMIN,
} from "../../../utils/constants";
import { Context } from "../../../utils/config";
import { FormulaTrackerErrorBoundary } from "./error-boundary";
import { StylistView } from "./StylistView";
import { AnalyticsView } from "./AnalyticsView";

const MAX_RANGE_DAYS = 366;

const dateFieldSx = {
  minWidth: 160,
  "& .MuiOutlinedInput-root": {
    backgroundColor: "background.paper",
    borderRadius: "6px",
  },
};

export function formatDateToISO(date) {
  if (!date || !dayjs(date).isValid()) return "";
  return dayjs(date).format("YYYY-MM-DD");
}

export function validateDateRange(startDate, endDate) {
  if (
    !startDate ||
    !endDate ||
    !dayjs(startDate).isValid() ||
    !dayjs(endDate).isValid()
  ) {
    toast.error("Please select both start and end dates", { duration: 3000 });
    return false;
  }
  if (dayjs(startDate).isAfter(dayjs(endDate), "day")) {
    toast.error("Start date must be before or equal to end date", {
      duration: 3000,
    });
    return false;
  }
  const daysDiff = dayjs(endDate).diff(dayjs(startDate), "day");
  if (daysDiff > MAX_RANGE_DAYS) {
    toast.error(`Date range cannot exceed ${MAX_RANGE_DAYS} days`, {
      duration: 3000,
    });
    return false;
  }
  return true;
}

/**
 * Formula Tracker — client formula lookup + analytics dashboard
 */
function FormulaTracker() {
  const { user } = useAuth();
  const { location: globalLocation } = useContext(Context);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const isStylist = user.userRoles?.includes(ROLE_STYLIST);
  const isAdmin =
    user.userRoles?.includes(ROLE_SALON_ADMIN) ||
    user.userRoles?.includes(ROLE_SYSTEM_ADMIN) ||
    user.userRoles?.includes(ROLE_JDTECH_ADMIN);

  const [activeView, setActiveView] = useState(isStylist ? "stylist" : "analytics");
  const [startDate, setStartDate] = useState(dayjs().subtract(30, "day"));
  const [endDate, setEndDate] = useState(dayjs());

  // Formula tracker does not support all-locations aggregate
  const effectiveSalon =
    globalLocation?.location_id !== -1 ? globalLocation : null;
  const needsLocation = !effectiveSalon;

  const filterProps = useMemo(
    () => ({
      salon: effectiveSalon,
      startDate,
      endDate,
      setStartDate,
      setEndDate,
      validateDateRange,
      formatDateToISO,
      user,
      needsLocation,
    }),
    [effectiveSalon, endDate, needsLocation, startDate, user]
  );

  const handleViewChange = useCallback((_event, value) => {
    setActiveView(value);
  }, []);

  return (
    <FormulaTrackerErrorBoundary>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box sx={{ px: 8, py: 3 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 2,
              mt: 1.5,
              mb: 2,
            }}
          >
            <Box>
              <Typography fontWeight={600} sx={{ fontSize: "1.5rem" }}>
                Formula Tracker
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Look up client formulas and review formula performance analytics.
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flexWrap: "wrap",
              }}
            >
              <DatePicker
                label="Start date"
                value={startDate}
                onChange={(value) => setStartDate(value)}
                slotProps={{ textField: { size: "small", sx: dateFieldSx } }}
              />
              <DatePicker
                label="End date"
                value={endDate}
                onChange={(value) => setEndDate(value)}
                maxDate={dayjs()}
                slotProps={{ textField: { size: "small", sx: dateFieldSx } }}
              />
            </Box>
          </Box>

          {needsLocation && (
            <Alert
              severity="info"
              sx={{
                mb: 2,
                borderRadius: "6px",
                border: "1px solid",
                borderColor: "primary.main",
              }}
            >
              Please select a location from the header to use Formula Tracker.
            </Alert>
          )}

          {isAdmin && (
            <Box
              sx={{
                borderBottom: "1px solid",
                borderColor: "divider",
                mb: 2.5,
              }}
            >
              <Tabs
                value={activeView}
                onChange={handleViewChange}
                sx={{
                  minHeight: 40,
                  "& .MuiTab-root": {
                    textTransform: "none",
                    fontWeight: 600,
                    minHeight: 40,
                  },
                  "& .Mui-selected": { color: "primary.main" },
                  "& .MuiTabs-indicator": { bgcolor: "primary.main" },
                }}
              >
                <Tab value="stylist" label="Stylist View" />
                <Tab value="analytics" label="Analytics" />
              </Tabs>
            </Box>
          )}

          {activeView === "analytics" && isMobile && (
            <Alert
              severity="warning"
              sx={{
                mb: 2,
                borderRadius: "6px",
                border: "1px solid",
                borderColor: "primary.main",
              }}
            >
              Analytics is best viewed on a desktop screen.
            </Alert>
          )}

          {activeView === "stylist" ? (
            <StylistView {...filterProps} />
          ) : (
            <AnalyticsView {...filterProps} />
          )}
        </Box>
      </LocalizationProvider>
    </FormulaTrackerErrorBoundary>
  );
}

export default FormulaTracker;
